import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import SideBarModal from '../modal/SideBarModal';
import { Section } from '../../interfaces';
import { getClientSection } from '../../utils/modal/paymentModal';
import {
  getPaymentLineItems,
  isCardPaymentMethod,
  isPaymentComplete,
} from '../../helpers/payments';
import PaymentLineItem from '../modal/PaymentLineItem';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import useUser from '../../hooks/global/useUser';
import useCancelCheckout from '../../hooks/payments/useCancelCheckout';
import { PaymentsGroup } from '../../types/payments';
import useGetPaymentPaymentsGroup from '../../hooks/payments/useGetPaymentPaymentsGroup';

const isPendingCredit = (group: PaymentsGroup) =>
  group.payments.some(
    p =>
      isCardPaymentMethod(p.method) &&
      (p.status === 'PENDING' || p.status === 'FAILED'),
  ) &&
  (group?.order?.checkoutSessionActive ||
    group?.appointment?.checkoutSessionActive);

type Props = {
  onClose: () => void;
} & (
  | {
      paymentsGroup: PaymentsGroup | null;
      onRefresh?: () => void | Promise<void>;
    }
  | { paymentId: string }
);

function PaymentsSideBarModal({ onClose, ...props }: Props) {
  const { user } = useUser();
  const navigate = useNavigate();
  const canCheckout = useFeatureGuard(Feature.CHECKOUT);
  const terminalReaderId = user?.business?.terminalReaderId;

  const {
    data: paymentPaymentsGroup,
    isPlaceholderData,
    refetch,
  } = useGetPaymentPaymentsGroup('paymentId' in props ? props.paymentId : null);

  const refresh = React.useCallback(async () => {
    if ('onRefresh' in props) {
      props.onRefresh?.();
    } else {
      await refetch();
    }
  }, [props, refetch]);

  const { mutateAsync: cancelCheckout, isPending: isLoadingCancel } =
    useCancelCheckout({
      onSuccess: refresh,
    });

  const paymentsGroup =
    'paymentsGroup' in props
      ? props.paymentsGroup
      : isPlaceholderData
      ? null
      : paymentPaymentsGroup;

  const navigateToCustomer = React.useCallback(
    (customerId: string) => {
      navigate(`/customers/${customerId}`);
    },
    [navigate],
  );

  const onCancelCheckout = React.useCallback(async () => {
    if (!paymentsGroup || isLoadingCancel) return;

    const isPending = isPendingCredit(paymentsGroup);
    if (!isPending) return;

    const paymentMethod =
      paymentsGroup.order?.paymentMethod ??
      paymentsGroup.appointment?.paymentMethod;

    const readerId =
      paymentMethod === 'TERMINAL' ? terminalReaderId : undefined;

    try {
      const response = await cancelCheckout({
        appointmentId: paymentsGroup.appointment?.appointmentId,
        orderId: paymentsGroup.order?.orderId,
        terminalReaderId: readerId,
      });

      if (response) {
        const successMsg =
          paymentMethod === 'TERMINAL'
            ? 'Terminal checkout canceled.'
            : 'Checkout canceled.';
        toast.success(successMsg);
      }
    } catch (cancelError) {
      const errorMsg =
        paymentMethod === 'TERMINAL'
          ? 'Error canceling terminal checkout'
          : 'Error canceling checkout.';
      toast.error(errorMsg);
    }
  }, [paymentsGroup, isLoadingCancel, cancelCheckout, terminalReaderId]);

  const sections = React.useMemo(() => {
    const tempSections: Section[] = [];
    if (!paymentsGroup) return tempSections;

    const isPending = isPendingCredit(paymentsGroup);
    const pendingMethod =
      paymentsGroup?.order?.paymentMethod ??
      paymentsGroup?.appointment?.paymentMethod;

    if (isPending) {
      tempSections.push({
        name: 'Payment ',
        rows: [
          {
            type: 'NODE',
            label: '',
            value: [
              <Stack
                spacing={2.5}
                width="100%"
                alignItems="center"
                justifyContent="center">
                {pendingMethod && (
                  <Typography variant="body1" align="center">
                    {pendingMethod === 'CREDIT'
                      ? 'Checkout link sent. Waiting on payment.'
                      : pendingMethod === 'TERMINAL'
                      ? 'Charge sent to payment terminal. Waiting on payment.'
                      : 'Payment pending...'}
                  </Typography>
                )}
                <CircularProgress />
                {canCheckout && (
                  <Box
                    display="flex"
                    justifyContent="space-around"
                    alignItems="center"
                    gap={2}
                    width="100%">
                    <Button
                      variant="text"
                      className="text-btn"
                      onClick={onCancelCheckout}
                      disabled={isLoadingCancel}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </Stack>,
            ],
          },
        ],
      });
    }

    const isComplete = paymentsGroup.payments.some(isPaymentComplete);
    if (isComplete) {
      tempSections.push({
        name: 'Details',
        rows: [
          {
            type: 'NODE',
            label: '',
            value: [
              <Stack spacing={1} width="100%">
                {getPaymentLineItems({
                  appointment: paymentsGroup?.appointment ?? null,
                  order: paymentsGroup?.order ?? null,
                  payments: paymentsGroup?.payments ?? [],
                }).map(({ label, amount, isTotal }) => (
                  <PaymentLineItem
                    key={label}
                    label={label}
                    value={Math.abs(amount)}
                    isTotal={isTotal}
                    subtract={amount < 0 || isTotal}
                  />
                ))}
              </Stack>,
            ],
          },
        ],
      });
    }

    tempSections.push({
      // DO NOT CHANGE NAME without changing SideBarSection.tsx
      name: 'Checkout Transactions',
      rows: [],
      values: {
        paymentsGroup,
      },
    });

    const hasTransfers = paymentsGroup.payments.some(
      payment => payment.transfers?.length,
    );
    if (hasTransfers)
      tempSections.push({
        // DO NOT CHANGE NAME without changing SideBarSection.tsx
        name: 'Checkout Transfer Details',
        rows: [],
        values: {
          paymentsGroup,
        },
      });

    const clientSection = getClientSection(paymentsGroup, {
      text: 'View',
      action: navigateToCustomer,
    });

    if (clientSection) tempSections.push(clientSection);

    return tempSections;
  }, [
    canCheckout,
    isLoadingCancel,
    navigateToCustomer,
    onCancelCheckout,
    paymentsGroup,
  ]);

  const isOpen =
    'paymentsGroup' in props ? !!sections?.length : !!props.paymentId;

  return (
    <SideBarModal
      isLoading={!paymentsGroup}
      modalOpen={isOpen}
      setModalOpen={onClose}
      name="Checkout Detail"
      sections={sections}
    />
  );
}

export default PaymentsSideBarModal;
